/**
 * Altura Global — Command Center → Analytics
 *
 * Executive-grade dashboard aggregating native CRM signals with GA4 traffic
 * data (once connected). All GA4-dependent tiles surface a clear "connect
 * GA4" empty-state until env vars land in production.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import {
  ArrowUpRight, ArrowDownRight, Users, Activity, Clock, FileText, Target,
  Globe2, Smartphone, Share2, RefreshCcw, Info, Sparkles, Calendar as CalIcon,
} from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import WorldMap, { topCountries } from "../../components/analytics/WorldMap";
import { api } from "../../lib/api";
import { analyticsConfig } from "../../lib/analytics";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { toast } from "sonner";

const RANGE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "90d", label: "Last 90 Days" },
  { key: "ytd", label: "This Year" },
];

function fmtDate(d) {
  if (!d) return "";
  const dd = d instanceof Date ? d : new Date(d);
  return dd.toISOString().slice(0, 10);
}

function fmtDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const CHART_COLORS = ["#3A1F14", "#C9A45A", "#8B5A3C", "#5C6E56", "#B45B4A", "#4A7C7E", "#7B6A55"];

const SOCIAL_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  youtube: "YouTube",
  pinterest: "Pinterest",
  x: "X (Twitter)",
};

function pct(a, b) {
  if (!b) return null;
  return ((a - b) / b) * 100;
}

function fmtSeconds(s) {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}m ${sec.toString().padStart(2, "0")}s`;
}

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat().format(Math.round(n));
}

function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null;
  const up = delta >= 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${up ? "bg-emerald-50 text-emerald-700" : "bg-altura-burgundy/10 text-altura-burgundy"}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, delta, testid }) {
  return (
    <div className="admin-card" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.24em] text-altura-charcoal/50">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-altura-gold shrink-0" />}
      </div>
      <div className="mt-2 font-editorial text-3xl md:text-4xl text-altura-espresso">{value}</div>
      <div className="mt-1.5 flex items-center gap-2">
        {sublabel && <div className="text-xs text-altura-charcoal/60">{sublabel}</div>}
        <DeltaBadge delta={delta} />
      </div>
    </div>
  );
}

function EmptyGA4({ note }) {
  return (
    <div className="rounded-md border border-dashed border-altura-gold/40 bg-altura-gold/5 p-4 text-sm text-altura-espresso/80" data-testid="ga4-empty-state">
      <div className="flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 text-altura-gold shrink-0" />
        <div>
          <div className="font-medium text-altura-espresso">Connect GA4 to unlock this tile</div>
          <div className="mt-1 text-xs text-altura-charcoal/70">
            {note || "Set GA4_PROPERTY_ID and GA4_SERVICE_ACCOUNT_JSON on the backend."}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, action, children, testid }) {
  return (
    <section className="admin-card" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-altura-gold" />}
          <h3 className="font-editorial text-xl text-altura-espresso">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function AdminAnalytics() {
  const [preset, setPreset] = useState("30d");
  const [customRange, setCustomRange] = useState({ from: undefined, to: undefined });
  const [compareEnabled, setCompareEnabled] = useState(true);
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState(null);
  const [pages, setPages] = useState(null);
  const [geo, setGeo] = useState(null);
  const [devices, setDevices] = useState(null);
  const [social, setSocial] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [visitorsTrend, setVisitorsTrend] = useState([]);
  const [leadsTrend, setLeadsTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    let qs = `preset=${preset}`;
    if (preset === "custom") {
      if (!customRange.from || !customRange.to) { setLoading(false); return; }
      qs = `preset=custom&start=${fmtDate(customRange.from)}&end=${fmtDate(customRange.to)}`;
    }
    try {
      const [s, ov, tr, tp, gg, dv, sc, fn, vt, lt] = await Promise.all([
        api.get(`/admin/analytics/status`),
        api.get(`/admin/analytics/overview?${qs}`),
        api.get(`/admin/analytics/traffic-sources?${qs}`),
        api.get(`/admin/analytics/top-pages?${qs}&limit=10`),
        api.get(`/admin/analytics/geography?${qs}&limit=20`),
        api.get(`/admin/analytics/devices?${qs}`),
        api.get(`/admin/analytics/social?${qs}`),
        api.get(`/admin/analytics/funnel?${qs}`),
        api.get(`/admin/analytics/trend?metric=visitors&${qs}`),
        api.get(`/admin/analytics/trend?metric=leads&${qs}`),
      ]);
      setStatus(s.data);
      setOverview(ov.data);
      setSources(tr.data);
      setPages(tp.data);
      setGeo(gg.data);
      setDevices(dv.data);
      setSocial(sc.data);
      setFunnel(fn.data);
      setVisitorsTrend(vt.data.points || []);
      setLeadsTrend(lt.data.points || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load analytics");
    } finally {
      setLoading(false);
    }
  }, [preset, customRange]);

  useEffect(() => { load(); }, [load]);

  const ga4Connected = overview?.ga4_configured;
  const ga = overview?.ga4 || {};
  const native = overview?.native || {};
  const gaPrev = ga.prev || {};
  const visitorsDelta = compareEnabled ? pct(ga.visitors, gaPrev.visitors) : null;
  const sessionsDelta = compareEnabled ? pct(ga.sessions, gaPrev.sessions) : null;
  const engagementDelta = compareEnabled ? pct(ga.engagement_rate, gaPrev.engagement_rate) : null;
  const durationDelta = compareEnabled ? pct(ga.average_session_duration, gaPrev.average_session_duration) : null;
  const pageViewsDelta = compareEnabled ? pct(ga.page_views, gaPrev.page_views) : null;

  const topSource = useMemo(() => {
    if (ga4Connected && sources?.ga4?.length) return sources.ga4[0]?.channel;
    if (sources?.native?.length) return sources.native[0]?.source;
    return "—";
  }, [ga4Connected, sources]);

  const insights = useMemo(() => buildInsights({ overview, sources, pages, funnel, social }), [overview, sources, pages, funnel, social]);
  // NOTE: `insights` retained as a lightweight fallback; the AI-powered brief
  // renders inside <AIInsights />.
  void insights;

  return (
    <div data-testid="page-admin-analytics">
      <AdminHeader
        breadcrumb="Operations"
        title="Analytics"
        subtitle="Executive view of traffic, engagement, and lead conversion — GA4 signals fused with native CRM truth."
      />

      <div className="p-4 md:p-8 space-y-6">
        {/* Range picker + status */}
        <div className="admin-card flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2 items-center">
            {RANGE_PRESETS.map((r) => (
              <button
                key={r.key}
                onClick={() => setPreset(r.key)}
                className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${preset === r.key ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  onClick={() => setPreset("custom")}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${preset === "custom" ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}
                  data-testid="range-custom"
                >
                  <CalIcon className="h-3.5 w-3.5" />
                  {customRange.from && customRange.to
                    ? `${fmtDateLabel(fmtDate(customRange.from))} – ${fmtDateLabel(fmtDate(customRange.to))}`
                    : "Custom"}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0" data-testid="custom-range-popover">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={customRange}
                  onSelect={(v) => setCustomRange(v || { from: undefined, to: undefined })}
                  disabled={{ after: new Date() }}
                  initialFocus
                />
                <div className="border-t border-altura-espresso/10 px-3 py-2 flex justify-between items-center text-xs">
                  <span className="text-altura-charcoal/60">
                    {customRange.from && customRange.to
                      ? `${Math.round((customRange.to - customRange.from) / 86400000) + 1} days`
                      : "Pick a start and end date"}
                  </span>
                  <button
                    className="text-altura-espresso hover:text-altura-gold uppercase tracking-widest"
                    onClick={() => setCustomRange({ from: undefined, to: undefined })}
                  >Clear</button>
                </div>
              </PopoverContent>
            </Popover>
            <label className="ml-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-altura-espresso cursor-pointer" data-testid="compare-toggle">
              <input
                type="checkbox"
                checked={compareEnabled}
                onChange={(e) => setCompareEnabled(e.target.checked)}
                className="accent-altura-gold"
              />
              Compare vs prior
            </label>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={status} />
            <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-full border border-altura-espresso/20 px-3 py-1.5 text-xs uppercase tracking-widest text-altura-espresso hover:border-altura-gold hover:text-altura-gold transition disabled:opacity-50" data-testid="analytics-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Comparison badge context strip — visible when compare is on */}
        {compareEnabled && ga4Connected && gaPrev.range?.start && (
          <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50 flex items-center gap-2" data-testid="compare-context">
            <span>Comparing vs</span>
            <span className="text-altura-espresso normal-case tracking-normal">
              {fmtDateLabel(gaPrev.range.start)} – {fmtDateLabel(gaPrev.range.end)}
            </span>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatCard testid="kpi-visitors" icon={Users} label="Total Visitors"
                    value={ga4Connected ? fmtNum(ga.visitors) : "—"} delta={ga4Connected ? visitorsDelta : null}
                    sublabel={ga4Connected ? `${fmtNum(ga.new_visitors)} new · ${fmtNum(ga.returning_visitors)} returning` : "Connect GA4"} />
          <StatCard testid="kpi-sessions" icon={Activity} label="Sessions"
                    value={ga4Connected ? fmtNum(ga.sessions) : "—"} delta={ga4Connected ? sessionsDelta : null}
                    sublabel={ga4Connected ? `${fmtNum(ga.engaged_sessions)} engaged` : "Connect GA4"} />
          <StatCard testid="kpi-engagement" icon={Target} label="Engagement Rate"
                    value={ga4Connected ? `${(ga.engagement_rate || 0).toFixed(1)}%` : "—"}
                    delta={ga4Connected ? engagementDelta : null}
                    sublabel={ga4Connected ? `${(100 - (ga.engagement_rate || 0)).toFixed(1)}% bounce` : "Connect GA4"} />
          <StatCard testid="kpi-duration" icon={Clock} label="Avg Session Duration"
                    value={ga4Connected ? fmtSeconds(ga.average_session_duration) : "—"}
                    delta={ga4Connected ? durationDelta : null}
                    sublabel={ga4Connected ? "Time on site" : "Connect GA4"} />
          <StatCard testid="kpi-pageviews" icon={FileText} label="Page Views"
                    value={ga4Connected ? fmtNum(ga.page_views) : "—"}
                    delta={ga4Connected ? pageViewsDelta : null}
                    sublabel={ga4Connected ? "Total across site" : "Connect GA4"} />
          <StatCard testid="kpi-conversion" icon={Target} label="Lead → Consultation"
                    value={`${(native.lead_conversion_rate || 0).toFixed(1)}%`}
                    sublabel={`${fmtNum(native.leads)} leads · ${fmtNum(native.consultations)} consults`} />
          <StatCard testid="kpi-inquiries" icon={Users} label="Inquiries"
                    value={fmtNum(native.leads)}
                    sublabel={`${fmtNum(native.form_submissions)} form submissions`} />
          <StatCard testid="kpi-top-source" icon={Share2} label="Top Traffic Source"
                    value={topSource || "—"}
                    sublabel={ga4Connected ? "From GA4 channel grouping" : "From CRM lead source"} />
        </div>

        {/* Trend charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Visitor Trend" icon={Activity} testid="chart-visitors">
            {!ga4Connected ? (
              <EmptyGA4 note="Visitor trends come from GA4 activeUsers by day." />
            ) : visitorsTrend.length === 0 ? (
              <div className="text-sm text-altura-charcoal/50 py-8 text-center">No traffic recorded in this range.</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visitorsTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5dfd7" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5b483e" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#5b483e" }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5dfd7", fontSize: 12 }} />
                    <Line type="monotone" dataKey="value" stroke="#C9A45A" strokeWidth={2} dot={{ r: 2 }} name="Visitors" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>
          <Section title="Leads Trend" icon={Users} testid="chart-leads">
            {leadsTrend.length === 0 ? (
              <div className="text-sm text-altura-charcoal/50 py-8 text-center">No new leads in this range.</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leadsTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5dfd7" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5b483e" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#5b483e" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5dfd7", fontSize: 12 }} />
                    <Bar dataKey="value" fill="#3A1F14" name="Leads" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>
        </div>

        {/* Funnel */}
        <Section title="Lead Funnel" icon={Target} testid="section-funnel">
          <Funnel stages={funnel?.stages || []} ga4Connected={ga4Connected} />
        </Section>

        {/* Traffic sources & Top pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Traffic Sources" icon={Share2} testid="section-sources">
            {ga4Connected ? (
              <SourcesTable rows={sources?.ga4 || []} />
            ) : (
              <div className="space-y-3">
                <EmptyGA4 note="Channel groupings (Organic / Direct / Social / Referral / Paid / Email) come from GA4." />
                <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50 mt-4">CRM Lead Sources (fallback)</div>
                <NativeSourcesTable rows={sources?.native || []} />
              </div>
            )}
          </Section>
          <Section title="Top Performing Pages" icon={FileText} testid="section-pages">
            {ga4Connected ? (
              <PagesTable rows={pages?.pages || []} />
            ) : (
              <EmptyGA4 note="Page-level metrics (views, engagement, bounce) come from GA4." />
            )}
          </Section>
        </div>

        {/* Geography & Devices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Geographic Distribution" icon={Globe2} testid="section-geo">
            {ga4Connected ? (
              <div className="space-y-4">
                <WorldMap rows={geo?.ga4 || []} height={360} testid="geo-world-map" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50 mb-2">Top Countries</div>
                  <GeoTable rows={topCountries(geo?.ga4 || [], 8)} />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <EmptyGA4 note="Countries / regions / cities of your visitors come from GA4." />
                <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50 mt-4">Lead Origin (from CRM)</div>
                <NativeGeoTable rows={geo?.native || []} />
              </div>
            )}
          </Section>
          <Section title="Devices & Browsers" icon={Smartphone} testid="section-devices">
            {ga4Connected ? (
              <DevicesGrid devices={devices?.devices || []} browsers={devices?.browsers || []} os={devices?.operating_systems || []} />
            ) : (
              <EmptyGA4 note="Desktop / Mobile / Tablet splits and browser/OS come from GA4." />
            )}
          </Section>
        </div>

        {/* Social performance */}
        <Section title="Social Performance" icon={Share2} testid="section-social">
          {ga4Connected ? (
            <SocialTable rows={social?.platforms || []} />
          ) : (
            <EmptyGA4 note="Referrals from Facebook, Instagram, LinkedIn, TikTok, YouTube, Pinterest, and X come from GA4 sessionSource." />
          )}
        </Section>

        {/* AI Insights */}
        <AIInsights compareEnabled={compareEnabled} />
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  const ga4Ok = status.ga4?.configured;
  // Measurement ID + Search Console token live on the frontend, so we source
  // them from process.env via analyticsConfig instead of the backend echo.
  const measurementOk = analyticsConfig.enabled;
  const scOk = Boolean(analyticsConfig.siteVerification);
  return (
    <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]" data-testid="status-pill">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${ga4Ok ? "bg-emerald-50 text-emerald-700" : "bg-altura-espresso/5 text-altura-espresso/60"}`}>GA4 Data · {ga4Ok ? "on" : "off"}</span>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${measurementOk ? "bg-emerald-50 text-emerald-700" : "bg-altura-espresso/5 text-altura-espresso/60"}`}>gtag · {measurementOk ? "on" : "off"}</span>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${scOk ? "bg-emerald-50 text-emerald-700" : "bg-altura-espresso/5 text-altura-espresso/60"}`}>Search Console · {scOk ? "on" : "off"}</span>
    </div>
  );
}

function Funnel({ stages, ga4Connected }) {
  if (!stages.length) return <div className="text-sm text-altura-charcoal/50 py-4">No funnel data.</div>;
  const max = Math.max(...stages.map((s) => s.value)) || 1;
  return (
    <div className="space-y-3">
      {stages.map((s, idx) => {
        const width = Math.max(6, (s.value / max) * 100);
        const isGa4 = idx <= 1;
        return (
          <div key={s.stage} data-testid={`funnel-${s.stage}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <div className="flex items-center gap-2 text-altura-espresso font-medium">
                <span className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50">{String(idx + 1).padStart(2, "0")}</span>
                {s.label}
                {isGa4 && !ga4Connected && <span className="text-[9px] uppercase tracking-widest text-altura-gold">GA4</span>}
              </div>
              <div className="flex items-center gap-3 text-altura-charcoal/70">
                <span className="font-editorial text-base text-altura-espresso">{fmtNum(s.value)}</span>
                {idx > 0 && s.conversion_from_previous !== undefined && (
                  <span className="text-[10px] uppercase tracking-widest text-altura-gold">{s.conversion_from_previous.toFixed(1)}% ↓</span>
                )}
              </div>
            </div>
            <div className="h-3 rounded-full bg-altura-espresso/5 overflow-hidden">
              <div className={`h-full ${isGa4 ? "bg-altura-gold/60" : "bg-altura-espresso"}`} style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SourcesTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-altura-charcoal/50 py-4">No traffic yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.15em] text-altura-charcoal/50">
          <tr className="border-b border-altura-espresso/10"><th className="text-left py-2">Channel</th><th className="text-right py-2">Visitors</th><th className="text-right py-2">Sessions</th><th className="text-right py-2">Conversions</th><th className="text-right py-2">CVR</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-altura-espresso/5">
              <td className="py-2 text-altura-espresso">{r.channel}</td>
              <td className="py-2 text-right">{fmtNum(r.visitors)}</td>
              <td className="py-2 text-right">{fmtNum(r.sessions)}</td>
              <td className="py-2 text-right">{fmtNum(r.conversions)}</td>
              <td className="py-2 text-right text-altura-charcoal/70">{(r.conversion_rate || 0).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NativeSourcesTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-altura-charcoal/50 py-4">No CRM sources yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.15em] text-altura-charcoal/50">
          <tr className="border-b border-altura-espresso/10"><th className="text-left py-2">Source</th><th className="text-right py-2">Leads</th><th className="text-right py-2">Conversions</th><th className="text-right py-2">CVR</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-altura-espresso/5">
              <td className="py-2 text-altura-espresso capitalize">{r.source.replace(/_/g, " ")}</td>
              <td className="py-2 text-right">{fmtNum(r.leads)}</td>
              <td className="py-2 text-right">{fmtNum(r.conversions)}</td>
              <td className="py-2 text-right text-altura-charcoal/70">{(r.conversion_rate || 0).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PagesTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-altura-charcoal/50 py-4">No page views yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.15em] text-altura-charcoal/50">
          <tr className="border-b border-altura-espresso/10"><th className="text-left py-2">Page</th><th className="text-right py-2">Views</th><th className="text-right py-2">Engagement</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-altura-espresso/5">
              <td className="py-2 max-w-[280px] truncate" title={r.path}>
                <div className="text-altura-espresso truncate">{r.title || r.path}</div>
                <div className="text-[11px] text-altura-charcoal/50 font-mono truncate">{r.path}</div>
              </td>
              <td className="py-2 text-right">{fmtNum(r.views)}</td>
              <td className="py-2 text-right text-altura-charcoal/70">{(r.engagement_rate || 0).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GeoTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-altura-charcoal/50 py-4">No geographic data yet.</div>;
  const collapsed = new Map();
  rows.forEach((r) => {
    const k = r.country || "(unknown)";
    collapsed.set(k, (collapsed.get(k) || 0) + (r.visitors || 0));
  });
  const list = [...collapsed.entries()].map(([country, v]) => ({ country, visitors: v })).sort((a, b) => b.visitors - a.visitors);
  const total = list.reduce((s, r) => s + r.visitors, 0) || 1;
  return (
    <div className="space-y-2">
      {list.slice(0, 12).map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-24 truncate text-sm text-altura-espresso">{r.country}</div>
          <div className="flex-1 h-2 rounded-full bg-altura-espresso/5 overflow-hidden">
            <div className="h-full bg-altura-espresso" style={{ width: `${(r.visitors / list[0].visitors) * 100}%` }} />
          </div>
          <div className="w-16 text-right text-xs text-altura-charcoal/70">{fmtNum(r.visitors)}</div>
          <div className="w-14 text-right text-[10px] uppercase tracking-widest text-altura-gold">{((r.visitors / total) * 100).toFixed(0)}%</div>
        </div>
      ))}
    </div>
  );
}

function NativeGeoTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-altura-charcoal/50 py-4">No lead origin data yet.</div>;
  const max = rows[0]?.leads || 1;
  return (
    <div className="space-y-2">
      {rows.slice(0, 10).map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-32 truncate text-sm text-altura-espresso">{r.country}</div>
          <div className="flex-1 h-2 rounded-full bg-altura-espresso/5 overflow-hidden">
            <div className="h-full bg-altura-gold" style={{ width: `${(r.leads / max) * 100}%` }} />
          </div>
          <div className="w-12 text-right text-xs text-altura-charcoal/70">{fmtNum(r.leads)}</div>
        </div>
      ))}
    </div>
  );
}

function DevicesGrid({ devices, browsers, os }) {
  const total = devices.reduce((s, d) => s + (d.visitors || 0), 0) || 1;
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50 mb-3">Device Category</div>
        <div className="flex gap-3">
          {devices.length === 0 && <div className="text-sm text-altura-charcoal/50">No data.</div>}
          {devices.length > 0 && (
            <div className="w-32 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={devices} dataKey="visitors" nameKey="device" innerRadius={30} outerRadius={55}>
                    {devices.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="flex-1 space-y-1.5">
            {devices.map((d, i) => (
              <div key={d.device} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="capitalize text-altura-espresso">{d.device}</span>
                </div>
                <div className="text-altura-charcoal/70 text-xs">
                  {fmtNum(d.visitors)} · {((d.visitors / total) * 100).toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50 mb-2">Browsers</div>
          <MiniList rows={browsers.slice(0, 5).map((b) => ({ label: b.browser, value: b.visitors }))} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50 mb-2">Operating Systems</div>
          <MiniList rows={os.slice(0, 5).map((o) => ({ label: o.os, value: o.visitors }))} />
        </div>
      </div>
    </div>
  );
}

function MiniList({ rows }) {
  if (!rows.length) return <div className="text-xs text-altura-charcoal/50">No data.</div>;
  return (
    <ul className="space-y-1">
      {rows.map((r, i) => (
        <li key={i} className="flex justify-between text-sm">
          <span className="text-altura-espresso truncate mr-2">{r.label}</span>
          <span className="text-altura-charcoal/70 text-xs shrink-0">{fmtNum(r.value)}</span>
        </li>
      ))}
    </ul>
  );
}

function SocialTable({ rows }) {
  const active = rows.filter((r) => r.visitors || r.sessions);
  if (!active.length) return <div className="text-sm text-altura-charcoal/50 py-4">No social traffic recorded yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.15em] text-altura-charcoal/50">
          <tr className="border-b border-altura-espresso/10"><th className="text-left py-2">Platform</th><th className="text-right py-2">Visitors</th><th className="text-right py-2">Sessions</th><th className="text-right py-2">Conversions</th></tr>
        </thead>
        <tbody>
          {active.map((r) => (
            <tr key={r.platform} className="border-b border-altura-espresso/5">
              <td className="py-2 text-altura-espresso">{SOCIAL_LABELS[r.platform] || r.platform}</td>
              <td className="py-2 text-right">{fmtNum(r.visitors)}</td>
              <td className="py-2 text-right">{fmtNum(r.sessions)}</td>
              <td className="py-2 text-right">{fmtNum(r.conversions)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildInsights({ overview, sources, pages, funnel, social }) {
  // Kept for reference / minor UI reuse — the primary insight system now lives
  // in the AIInsights component below, powered by Claude Sonnet 4.5.
  const out = [];
  const ga4Connected = overview?.ga4_configured;
  const ga = overview?.ga4;
  const native = overview?.native;
  if (ga4Connected && ga?.visitors > 0 && ga?.prev?.visitors > 0) {
    const delta = ((ga.visitors - ga.prev.visitors) / ga.prev.visitors) * 100;
    out.push({ tag: "Traffic", text: `Visitors ${delta >= 0 ? "up" : "down"} ${Math.abs(delta).toFixed(1)}% vs the prior period.` });
  }
  if (native?.leads > 0) {
    out.push({ tag: "Pipeline", text: `${native.leads} inquiries captured this range.` });
  }
  return out;
}

const SEVERITY_STYLES = {
  critical: { badge: "bg-altura-burgundy text-altura-ivory", border: "border-altura-burgundy", accent: "text-altura-burgundy" },
  warning: { badge: "bg-altura-gold/20 text-altura-espresso", border: "border-altura-gold", accent: "text-altura-gold" },
  positive: { badge: "bg-emerald-100 text-emerald-800", border: "border-emerald-500", accent: "text-emerald-700" },
  neutral: { badge: "bg-altura-espresso/10 text-altura-espresso", border: "border-altura-espresso/30", accent: "text-altura-espresso" },
};

function AIInsights({ compareEnabled }) {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [preset, setPreset] = useState("7d");
  const [sendEmail, setSendEmail] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/analytics/insights?limit=10`);
      setHistory(data.items || []);
      if (!latest && data.items?.[0]) setLatest(data.items[0]);
    } catch { /* silent */ }
  }, [latest]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/analytics/insights/generate?preset=${preset}&send_email=${sendEmail}`);
      setLatest(data);
      toast.success(sendEmail ? "Fresh brief generated + emailed" : "Fresh brief generated");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Insight generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Section
      title="Executive Intelligence"
      icon={Sparkles}
      testid="section-insights"
      action={
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="text-xs uppercase tracking-widest bg-white border border-altura-espresso/20 rounded-full px-3 py-1.5 text-altura-espresso"
            data-testid="insights-preset"
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="ytd">This Year</option>
          </select>
          <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-altura-espresso">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-altura-gold" />
            Email me
          </label>
          <button
            onClick={generate}
            disabled={generating}
            className="inline-flex items-center gap-2 rounded-full bg-altura-espresso text-altura-ivory px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-altura-espresso-2 transition disabled:opacity-50"
            data-testid="insights-generate"
          >
            <Sparkles className={`h-3.5 w-3.5 ${generating ? "animate-pulse" : ""}`} />
            {generating ? "Generating…" : "Generate brief"}
          </button>
        </div>
      }
    >
      {!latest && (
        <div className="text-sm text-altura-charcoal/50 py-6 text-center" data-testid="insights-empty">
          No briefs generated yet. Click <em>Generate brief</em> to produce your first executive summary — Claude Sonnet 4.5 will analyze your last-{preset} performance and hand back actionable recommendations.
        </div>
      )}

      {latest && (
        <div data-testid="latest-insight">
          {/* Headline + score */}
          <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-altura-espresso/10">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.24em] text-altura-charcoal/50">
                Generated {new Date(latest.generated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                {latest.range?.start && <> · {latest.range.start} → {latest.range.end}</>}
              </div>
              <div className="mt-1.5 font-editorial text-lg text-altura-espresso leading-snug">{latest.headline}</div>
            </div>
            <div className="text-center min-w-[80px]">
              <div className="text-[10px] uppercase tracking-[0.24em] text-altura-charcoal/50">Score</div>
              <div className={`font-editorial text-3xl ${latest.score >= 70 ? "text-emerald-600" : latest.score >= 40 ? "text-altura-gold" : "text-altura-burgundy"}`}>
                {latest.score}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-altura-charcoal/40">/ 100</div>
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {(latest.cards || []).map((c) => {
              const s = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.neutral;
              return (
                <div key={c.id} className={`rounded-md border-l-4 ${s.border} bg-altura-ivory-2/50 p-4`} data-testid={`insight-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${s.badge}`}>
                      {c.tag}
                    </span>
                    <span className={`text-[10px] uppercase tracking-widest ${s.accent}`}>{c.severity}</span>
                  </div>
                  <div className="font-editorial text-base text-altura-espresso">{c.title}</div>
                  <div className="mt-2 text-sm text-altura-charcoal/85 leading-relaxed">{c.observation}</div>
                  <div className="mt-3 text-sm text-altura-espresso leading-relaxed">
                    <span className="text-[10px] uppercase tracking-widest text-altura-gold">Recommendation</span>
                    <div className="mt-0.5">{c.recommendation}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* History */}
          {history.length > 1 && (
            <div className="mt-6 pt-4 border-t border-altura-espresso/10">
              <button
                onClick={() => setShowHistory((v) => !v)}
                className="text-xs uppercase tracking-widest text-altura-espresso hover:text-altura-gold"
                data-testid="insights-toggle-history"
              >
                {showHistory ? "Hide" : "Show"} previous briefs ({history.length - 1})
              </button>
              {showHistory && (
                <ul className="mt-3 space-y-2">
                  {history.filter((h) => h.id !== latest.id).map((h) => (
                    <li key={h.id}>
                      <button
                        onClick={() => setLatest(h)}
                        className="text-left w-full rounded-md border border-altura-espresso/10 hover:border-altura-gold p-3 transition"
                        data-testid={`insight-history-${h.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-widest text-altura-charcoal/50">
                          {new Date(h.generated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} · {h.preset} · score {h.score}
                        </div>
                        <div className="text-sm text-altura-espresso mt-0.5 line-clamp-2">{h.headline}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
